/* ==========================================================================
   Blank Brim Supply — Theme JS (vanilla, no dependencies)
   Behaviors are bound by data attributes so sections stay decoupled:
     - [data-drawer-open="<id>"] / [data-drawer-close]  → drawer nav
     - form[data-product-form] + script[data-product-json] → variant picker
     - [data-qty-change] + [data-qty-input]              → quantity steppers
     - [data-gallery] / [data-gallery-thumb]             → product gallery
     - form[data-cart-auto-submit]                       → cart qty updates
     - [data-sticky-header]                              → scrolled state
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- Helpers ---------- */

  function formatMoney(cents) {
    var format = (window.BBS && window.BBS.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  /* Approved wholesale accounts (signed in): the layout sets window.BBS.memberUnitCents from
     snippets/member-price.liquid. Every price JS paints goes through here: min(price, member unit).
     Guests: no memberUnitCents → the price is returned untouched. */
  function displayCents(cents) {
    var m = window.BBS && window.BBS.memberUnitCents;
    return (m && cents > m) ? m : cents;
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- Drawer (mobile menu) ---------- */

  function initDrawers() {
    document.querySelectorAll('[data-drawer-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
        if (!drawer) return;
        drawer.classList.add('is-open');
        document.body.classList.add('drawer-open');
        trigger.setAttribute('aria-expanded', 'true');
        var firstLink = drawer.querySelector('a, button:not([data-drawer-close])');
        if (firstLink) firstLink.focus();
      });
    });

    document.querySelectorAll('.drawer').forEach(function (drawer) {
      function close() {
        drawer.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
        document.querySelectorAll('[data-drawer-open="' + drawer.id + '"]').forEach(function (t) {
          t.setAttribute('aria-expanded', 'false');
        });
      }
      drawer.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', close);
      });
      var overlay = drawer.querySelector('.drawer__overlay');
      if (overlay) overlay.addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });
    });
  }

  /* ---------- Sticky header scrolled state ---------- */

  function initStickyHeader() {
    var header = document.querySelector('[data-sticky-header]');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Quantity steppers ---------- */

  function initQuantitySteppers(root) {
    (root || document).querySelectorAll('[data-qty-change]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.qty') || btn.parentElement;
        var input = wrap ? wrap.querySelector('[data-qty-input]') : null;
        if (!input) return;
        var step = parseInt(btn.getAttribute('data-qty-change'), 10) || 1;
        var min = parseInt(input.getAttribute('min'), 10);
        if (isNaN(min)) min = 1;
        var current = parseInt(input.value, 10);
        if (isNaN(current)) current = min;
        var next = Math.max(current + step, min);
        if (String(next) !== input.value) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ---------- Product form: variant picker ---------- */

  function initProductForms() {
    document.querySelectorAll('form[data-product-form]').forEach(function (form) {
      var section = form.closest('[data-product-section]') || form;
      var jsonTag = section.querySelector('script[data-product-json]');
      if (!jsonTag) return;

      var product;
      try {
        product = JSON.parse(jsonTag.textContent);
      } catch (err) {
        return;
      }

      var idInput = form.querySelector('input[name="id"]');
      var selects = Array.prototype.slice.call(section.querySelectorAll('[data-option-select]'));
      var priceEl = section.querySelector('[data-price]');
      var compareEl = section.querySelector('[data-compare-price]');
      var button = form.querySelector('[data-atc-button]');
      var buttonText = form.querySelector('[data-atc-text]');
      var skuEl = section.querySelector('[data-variant-sku]');

      function currentVariant() {
        var chosen = selects.map(function (s) { return s.value; });
        return product.variants.find(function (v) {
          return chosen.every(function (val, i) { return v.options[i] === val; });
        });
      }

      function updateMedia(variant) {
        if (!variant || !variant.featured_media) return;
        var thumb = section.querySelector('[data-gallery-thumb][data-media-id="' + variant.featured_media.id + '"]');
        if (thumb) thumb.click();
      }

      function render() {
        var variant = currentVariant();
        var strings = (window.BBS && window.BBS.strings) || {};

        if (!variant) {
          if (button) button.disabled = true;
          if (buttonText) buttonText.textContent = strings.unavailable || 'Unavailable';
          return;
        }

        if (idInput) idInput.value = variant.id;
        var shownCents = displayCents(variant.price);
        if (priceEl) priceEl.textContent = formatMoney(shownCents);
        if (compareEl) {
          if (shownCents === variant.price && variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (skuEl) skuEl.textContent = variant.sku || '';
        if (button) button.disabled = !variant.available;
        if (buttonText) {
          buttonText.textContent = variant.available
            ? (strings.addToCart || 'Add to cart')
            : (strings.soldOut || 'Sold out');
        }
        updateMedia(variant);
        section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant }, bubbles: true }));

        // Keep the URL shareable per variant
        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      selects.forEach(function (s) { s.addEventListener('change', render); });
      if (selects.length) render();
    });
  }

  /* ---------- Product gallery ---------- */

  function initGalleries() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('[data-gallery-main] img');
      if (!main) return;
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-src');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          if (src) main.src = src;
          if (srcset) main.srcset = srcset; else main.removeAttribute('srcset');
          main.alt = alt;
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
            t.classList.toggle('is-active', t === thumb);
          });
        });
      });
    });
  }

  /* ---------- Cart: auto submit on quantity change ---------- */

  function initCartForms() {
    document.querySelectorAll('form[data-cart-auto-submit]').forEach(function (form) {
      var timer = null;
      form.addEventListener('change', function (e) {
        if (!e.target.matches('[data-qty-input]')) return;
        // Wait for the buyer to stop clicking +/- before reloading, so quick clicks are not lost.
        clearTimeout(timer);
        timer = setTimeout(function () { form.submit(); }, 650);
      });
    });
  }

  /* ---------- Boot ---------- */

  onReady(function () {
    initDrawers();
    initStickyHeader();
    initQuantitySteppers(document);
    initProductForms();
    initGalleries();
    initCartForms();
  });

  // Re-init when sections are re-rendered inside the Theme Editor
  document.addEventListener('shopify:section:load', function () {
    initDrawers();
    initStickyHeader();
    initQuantitySteppers(document);
    initProductForms();
    initGalleries();
    initCartForms();
  });
})();

/* ==========================================================================
   v2 — melin-level behaviors. Contracts (see CONVENTIONS.md):
     - form[data-ajax-cart]                  → AJAX add-to-cart + open cart drawer
     - #cart-drawer.drawer.drawer--right     → cart drawer (section 'cart-drawer')
     - [data-cart-count]                     → live item-count badges
     - [data-cart-line-qty] inputs (drawer)  → /cart/change.js by line key
     - [data-predictive-input] + [data-predictive-panel] → predictive search
     - [data-modal-open="id"] / .modal       → accessible modals
     - [data-sticky-bar] + [data-sticky-anchor] → sticky product bar
     - 'variant:change' CustomEvent          → dispatched by product form render()
     - [data-recently-viewed] / BBS.recentlyViewed → localStorage product cards
     - [data-rail] > .scroll-row + .rail__btn → carousel arrows
     - .reveal                                → scroll-in animation
   ========================================================================== */
(function () {
  'use strict';

  var onReady = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* Wholesale member display price (same rule as displayCents() in the first block; separate scope). */
  function displayCents(cents) {
    var m = window.BBS && window.BBS.memberUnitCents;
    return (m && cents > m) ? m : cents;
  }
  function formatCents(cents) {
    var f = (window.BBS && window.BBS.moneyFormat) || '${{amount}}';
    return f.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, (cents / 100).toFixed(2));
  }

  /* ---------- Cart helpers ---------- */

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('header__cart-count--empty', count === 0);
    });
  }

  function refreshCartDrawer(openAfter) {
    var existing = document.getElementById('cart-drawer');
    var wasOpen = !!(existing && existing.classList.contains('is-open'));
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var holder = document.getElementById('shopify-section-cart-drawer');
        if (!holder) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('#shopify-section-cart-drawer') || tmp.firstElementChild;
        holder.innerHTML = fresh ? fresh.innerHTML : html;
        bindCartDrawer();
        if (openAfter || wasOpen) openDrawerById('cart-drawer');
      })
      .catch(function () {});
  }

  function openDrawerById(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.classList.add('drawer-open');
  }

  /* AJAX add to cart */
  function initAjaxCart() {
    if (window.__bbsAjaxCartBound) return;
    window.__bbsAjaxCartBound = true;
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches('form[data-ajax-cart]')) return;
      e.preventDefault();
      var button = form.querySelector('[type="submit"]');
      if (button) { button.classList.add('is-loading'); button.disabled = true; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () { return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          return refreshCartDrawer(true);
        })
        .catch(function (err) {
          var msg = (err && err.description) || (window.BBS && window.BBS.strings && window.BBS.strings.addError) || 'Could not add to cart';
          var status = form.querySelector('[data-form-status]');
          if (status) { status.textContent = msg; status.hidden = false; }
          else window.location.href = (window.BBS && window.BBS.routes && window.BBS.routes.cart) || '/cart';
        })
        .finally(function () {
          if (button) { button.classList.remove('is-loading'); button.disabled = false; }
        });
    });
  }

  /* Discount code in cart ([data-cart-discount], snippets/cart-discount-code.liquid).
     POST /cart/update.js { discount } → drawer re-renders, cart page reloads. */
  function initCartDiscount() {
    if (window.__bbsCartDiscountBound) return;
    window.__bbsCartDiscountBound = true;
    function setMsg(box, text, ok) {
      var msg = box.querySelector('[data-cart-discount-msg]');
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.classList.toggle('cart-code__msg--ok', !!ok);
    }
    /* Localized messages are rendered server-side as data-msg-* attributes (snippets/cart-discount-code.liquid). */
    function msgFor(box, name, fallback) {
      var v = box.getAttribute('data-msg-' + name);
      return v ? v : fallback;
    }
    function send(box, code) {
      var ctx = box.getAttribute('data-cart-discount-context');
      var min = parseInt(box.getAttribute('data-cart-discount-min'), 10) || 0;
      var count = parseInt(box.getAttribute('data-cart-count-now'), 10) || 0;
      var wanted = (code || '').trim().toUpperCase();
      box.classList.add('is-loading');
      setMsg(box, '');
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: wanted })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var codes = cart.discount_codes || [];
          var hit = null;
          codes.forEach(function (c) { if (String(c.code).toUpperCase() === wanted) hit = c; });
          if (wanted && (!hit || !hit.applicable)) {
            box.classList.remove('is-loading');
            if (min && count < min) setMsg(box, msgFor(box, 'min', 'This code applies to orders of %MIN% caps or more. Add %REM% more.').replace(/%MIN%/g, String(min)).replace(/%REM%/g, String(min - count)));
            else setMsg(box, msgFor(box, 'not-applicable', "This code can't be applied to your cart."));
            if (hit) return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ discount: '' }) }).catch(function () {});
            return;
          }
          updateCartCount(cart.item_count);
          if (ctx === 'drawer' && document.getElementById('cart-drawer')) return refreshCartDrawer(false).then(function () { if (document.body.classList.contains('template-cart') || window.location.pathname.indexOf('/cart') === 0) window.location.reload(); });
          window.location.reload();
        })
        .catch(function () { box.classList.remove('is-loading'); setMsg(box, msgFor(box, 'generic', 'Something went wrong. Please try again.')); });
    }
    document.addEventListener('click', function (e) {
      var apply = e.target.closest('[data-cart-discount-apply]');
      var remove = e.target.closest('[data-cart-discount-remove]');
      if (!apply && !remove) return;
      var box = e.target.closest('[data-cart-discount]');
      if (!box) return;
      if (remove) { send(box, ''); return; }
      var input = box.querySelector('[data-cart-discount-input]');
      var code = input ? input.value : '';
      if (!code.trim()) { setMsg(box, msgFor(box, 'empty', 'Enter a discount code.')); if (input) input.focus(); return; }
      send(box, code);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || !e.target.matches('[data-cart-discount-input]')) return;
      e.preventDefault();
      var box = e.target.closest('[data-cart-discount]');
      if (box) send(box, e.target.value);
    });
  }

  /* Cart drawer line updates */
  var lineQtyTimers = {};
  var lineQtySeq = 0;
  function sendLineQty(key, qty) {
    var seq = ++lineQtySeq;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (seq !== lineQtySeq) return; // a newer change is in flight; it will re-render
        updateCartCount(cart.item_count);
        refreshCartDrawer(false);
      })
      .catch(function () { if (seq === lineQtySeq) refreshCartDrawer(false); });
  }
  function bindCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    initQuantityRebind(drawer);
    drawer.querySelectorAll('[data-cart-line-qty]').forEach(function (input) {
      if (input.dataset.lineBound) return;
      input.dataset.lineBound = 'true';
      input.addEventListener('change', function () {
        // Debounce per line: several quick +/- clicks become ONE /cart/change.js with the final quantity,
        // and only the latest response re-renders the drawer (older ones would make the count jump back).
        var key = input.getAttribute('data-line-key');
        var qty = parseInt(input.value, 10) || 0;
        clearTimeout(lineQtyTimers[key]);
        lineQtyTimers[key] = setTimeout(function () { sendLineQty(key, qty); }, 450);
      });
    });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      if (btn.dataset.closeBound) return;
      btn.dataset.closeBound = 'true';
      btn.addEventListener('click', function () {
        drawer.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
      });
    });
    var overlay = drawer.querySelector('.drawer__overlay');
    if (overlay && !overlay.dataset.closeBound) {
      overlay.dataset.closeBound = 'true';
      overlay.addEventListener('click', function () {
        drawer.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
      });
    }
    drawer.querySelectorAll('[data-cart-line-remove]').forEach(function (btn) {
      if (btn.dataset.removeBound) return;
      btn.dataset.removeBound = 'true';
      btn.addEventListener('click', function () {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: btn.getAttribute('data-line-key'), quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            refreshCartDrawer(false);
          })
          .catch(function () { refreshCartDrawer(false); });
      });
    });
    if (!window.__bbsDrawerEscBound) {
      window.__bbsDrawerEscBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = document.querySelectorAll('.drawer.is-open');
        if (!open.length) return;
        open.forEach(function (d) { d.classList.remove('is-open'); });
        document.body.classList.remove('drawer-open');
      });
    }
  }

  function initQuantityRebind(root) {
    root.querySelectorAll('[data-qty-change]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.qty') || btn.parentElement;
        var input = wrap ? wrap.querySelector('[data-qty-input]') : null;
        if (!input) return;
        var step = parseInt(btn.getAttribute('data-qty-change'), 10) || 1;
        var min = parseInt(input.getAttribute('min'), 10);
        if (isNaN(min)) min = 1;
        var current = parseInt(input.value, 10);
        if (isNaN(current)) current = min;
        var next = Math.max(current + step, min);
        if (String(next) !== input.value) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch() {
    document.querySelectorAll('[data-predictive-input]').forEach(function (input) {
      if (input.dataset.predBound) return;
      input.dataset.predBound = 'true';
      var panel = document.querySelector('[data-predictive-panel="' + (input.getAttribute('data-predictive-input') || 'default') + '"]') ||
                  input.closest('form') && input.closest('form').parentElement.querySelector('[data-predictive-panel]');
      if (!panel) return;
      var timer = null;
      input.addEventListener('input', function () {
        clearTimeout(timer);
        var q = input.value.trim();
        if (q.length < 2) { panel.hidden = true; return; }
        timer = setTimeout(function () {
          fetch(((window.BBS && window.BBS.routes && window.BBS.routes.predictiveSearch) || '/search/suggest') + '?q=' + encodeURIComponent(q) + '&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search')
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              var inner = tmp.querySelector('[data-predictive-results]');
              panel.innerHTML = inner ? inner.innerHTML : '';
              panel.hidden = !panel.innerHTML.trim();
            })
            .catch(function () { panel.hidden = true; });
        }, 220);
      });
      document.addEventListener('click', function (e) {
        if (!panel.contains(e.target) && e.target !== input) panel.hidden = true;
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') panel.hidden = true;
      });
    });
  }

  /* ---------- Modals ---------- */
  function initModals() {
    if (window.__bbsModalsBound) return;
    window.__bbsModalsBound = true;
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-modal-open]');
      if (opener) {
        var modal = document.getElementById(opener.getAttribute('data-modal-open'));
        if (modal) {
          modal.classList.add('is-open');
          document.body.classList.add('modal-open');
          var focusable = modal.querySelector('button, a, input, [tabindex]');
          if (focusable) focusable.focus();
        }
      }
      if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal__overlay')) {
        var open = e.target.closest('.modal') || document.querySelector('.modal.is-open');
        if (open) {
          open.classList.remove('is-open');
          document.body.classList.remove('modal-open');
        }
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('.modal.is-open');
      if (open) {
        open.classList.remove('is-open');
        document.body.classList.remove('modal-open');
      }
    });
  }

  /* ---------- Sticky product bar ---------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-bar]');
    var anchor = document.querySelector('[data-sticky-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);
    if (window.__bbsStickyVariantBound) return;
    window.__bbsStickyVariantBound = true;
    document.addEventListener('variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      var bar = document.querySelector('[data-sticky-bar]');
      if (!v || !bar) return;
      var price = bar.querySelector('[data-sticky-price]');
      if (price && window.BBS) {
        price.textContent = (function (cents) {
          var f = (window.BBS && window.BBS.moneyFormat) || '${{amount}}';
          return f.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, (cents / 100).toFixed(2));
        })(displayCents(v.price));
      }
      var meta = bar.querySelector('[data-sticky-variant]');
      if (meta) meta.textContent = v.title === 'Default Title' ? '' : v.title;
    });
  }

  /* ---------- Recently viewed (localStorage, no fetches) ---------- */
  var RV_KEY = 'bbs:recently-viewed';
  function rvStore(item) {
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter(function (p) { return p.handle !== item.handle; });
      list.unshift(item);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (err) {}
  }
  function initRecentlyViewed() {
    var meta = document.querySelector('script[data-rv-product]');
    if (meta) {
      try { rvStore(JSON.parse(meta.textContent)); } catch (err) {}
    }
    /* Header eye badge: count of viewed products for the current storefront brand */
    var badges = document.querySelectorAll('[data-rv-count]');
    if (badges.length) {
      var badgeList;
      try { badgeList = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (err) { badgeList = []; }
      var badgeBrand = badges[0].getAttribute('data-rv-count-brand');
      if (badgeBrand) {
        badgeList = badgeList.filter(function (p) { return (p.brand || 'blank_brim') === badgeBrand; });
      }
      badges.forEach(function (b) {
        b.textContent = badgeList.length;
        b.classList.toggle('header__cart-count--empty', badgeList.length === 0);
      });
    }
    document.querySelectorAll('[data-recently-viewed]').forEach(function (host) {
      var list;
      try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (err) { list = []; }
      var current = host.getAttribute('data-exclude-handle');
      var brand = host.getAttribute('data-current-brand');
      list = list.filter(function (p) { return p.handle !== current; });
      if (brand) {
        list = list.filter(function (p) { return (p.brand || 'blank_brim') === brand; });
      }
      list = list.slice(0, parseInt(host.getAttribute('data-limit'), 10) || 8);
      if (!list.length) {
        /* Hosts that DECLARE an empty message (the header drawer) show it instead
           of hiding — hiding would remove the whole header section. Presence of
           the attribute is what matters: a cleared setting must never fall
           through to the hide branch. */
        if (host.hasAttribute('data-rv-empty-text')) {
          var emptyRow = host.querySelector('[data-rv-row]') || host;
          var emptyP = document.createElement('p');
          emptyP.className = 'text-muted';
          emptyP.style.gridColumn = '1 / -1';
          emptyP.textContent = host.getAttribute('data-rv-empty-text');
          emptyRow.innerHTML = '';
          emptyRow.appendChild(emptyP);
          return;
        }
        var sec = host.closest('.shopify-section') || host;
        sec.style.display = 'none';
        return;
      }
      var row = host.querySelector('[data-rv-row]') || host;
      row.innerHTML = list.map(function (p) {
        /* Stored price is the public one ("$20.99" + cents); members see theirs. */
        var shown = p.price || '';
        if (window.BBS && window.BBS.memberUnitCents) {
          var c = typeof p.cents === 'number' ? p.cents : Math.round(parseFloat(String(p.price || '').replace(/[^0-9.]/g, '')) * 100);
          if (c > 0) shown = formatCents(displayCents(c));
        }
        return '<article class="product-card">' +
          '<div class="product-card__media product-card__media--square">' +
          (p.image ? '<img class="product-card__img--main" src="' + p.image + '" alt="' + (p.title || '') + '" loading="lazy">' : '') +
          '</div><div class="product-card__info">' +
          '<h3 class="product-card__title"><a href="' + p.url + '">' + p.title + '</a></h3>' +
          '<div class="product-card__price"><span class="price"><span class="price__regular">' + shown + '</span></span></div>' +
          '</div></article>';
      }).join('');
    });
  }

  /* ---------- Rails (carousel arrows) ---------- */
  function initRails() {
    document.querySelectorAll('[data-rail]').forEach(function (rail) {
      if (rail.dataset.railBound) return;
      rail.dataset.railBound = 'true';
      var row = rail.querySelector('.scroll-row');
      if (!row) return;
      var prev = rail.querySelector('.rail__btn--prev');
      var next = rail.querySelector('.rail__btn--next');
      function page(dir) {
        row.scrollBy({ left: dir * row.clientWidth * 0.9, behavior: 'smooth' });
      }
      function sync() {
        if (prev) prev.disabled = row.scrollLeft <= 4;
        if (next) next.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
      }
      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });
      row.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.reveal').forEach(function (el) { io.observe(el); });
  }

  function boot() {
    initAjaxCart();
    initCartDiscount();
    bindCartDrawer();
    initPredictiveSearch();
    initModals();
    initStickyBar();
    initRecentlyViewed();
    initRails();
    initReveal();
  }
  onReady(boot);
  document.addEventListener('shopify:section:load', boot);
})();
